import React, { useState } from 'react';
import { API_BASE_URL } from '../../constants/property';

const defaultSettings = {
  defaultTab: 'plant', // plant | site
  showFiltersByDefault: true,
  itemsPerPageDefault: 10,
  pageSizeOptions: [5, 10, 20, 50],

  enablePDFExport: true,
  enableExcelExport: true,
  enableCSVExport: false,

  datePresets: {
    today: true,
    yesterday: true,
    last7: true,
    last30: true,
    thisMonth: true,
    lastMonth: true,
    custom: true,
  },

  vendors: ['MMR', 'RRK', 'National', 'JJ', 'TRN', 'LLP', 'JJN', 'Arjun'],
  siteCodes: ['ST1998', 'ST2765', 'ST3865', 'ST4765', 'ST19098', 'ST2003'],
  plantMaterials: ['MSAND', 'Aggregate', 'Cement', 'Dust', 'Bitman(Thar)', 'LDO Oil', 'Diesel', 'RMC', 'BT'],
  siteMaterials: ['Steel', 'Aggregate', 'Cement', 'PVC Pipes', 'Wires', 'FRP Manual Door', 'FRP Catchpit', 'CI Catchpit', 'FRP RWH', 'RMC', 'BT'],

  requireQtyUnitForSite: ['cement', 'pvcpipes', 'wires'],
  enableWeightFieldsForBT: true,

  // GST Settings
  gstPercentage: 5, // Default GST percentage
};

const ChipList = ({ items, onRemove }) => (
  <div className="flex flex-wrap gap-2">
    {items.map((item) => (
      <span key={item} className="inline-flex items-center gap-2 rounded border px-2 py-1">
        {item}
        <button onClick={() => onRemove(item)} className="text-red-600" title="Remove">×</button>
      </span>
    ))}
  </div>
);

const AddInline = ({ placeholder, onAdd, type = 'text' }) => (
  <div className="flex items-center gap-2">
    <input
      type={type}
      placeholder={placeholder}
      className="rounded-md border px-3 py-2"
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          const val = e.currentTarget.value.trim();
          if (val) onAdd(val);
          e.currentTarget.value = '';
        }
      }}
    />
    <button
      className="px-3 py-2 rounded-md border hover:bg-gray-50"
      onClick={(e) => {
        const input = e.currentTarget.previousSibling;
        if (input && input.value.trim()) {
          onAdd(input.value.trim());
          input.value = '';
        }
      }}
    >Add</button>
  </div>
);

const StockInSettings = () => {
  const [settings, setSettings] = useState(defaultSettings);
  const [saving, setSaving] = useState(false);

  const update = (key, value) => setSettings((s) => ({ ...s, [key]: value }));
  const togglePreset = (key) => update('datePresets', { ...settings.datePresets, [key]: !settings.datePresets[key] });

  const addUnique = (listKey, value) => {
    update(listKey, Array.from(new Set([...(settings[listKey] || []), value])));
  };
  const removeItem = (listKey, value) => {
    update(listKey, (settings[listKey] || []).filter((v) => v !== value));
  };

  const addPageSize = (n) => {
    const num = Number(n);
    if (!Number.isFinite(num) || num <= 0) return;
    const next = Array.from(new Set([...(settings.pageSizeOptions || []), num])).sort((a, b) => a - b);
    update('pageSizeOptions', next);
  };
  const removePageSize = (n) => update('pageSizeOptions', (settings.pageSizeOptions || []).filter((v) => v !== n));

  // Helper to create material type via API
  const createMaterialType = async (materialType, contextType) => {
    try {
      const payload = {
        materialType,
        materialGrade: [materialType],
        materialUnit: [materialType],
        type: contextType,
      };

      const response = await fetch(`${API_BASE_URL}/api/material`, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error creating material type via API:', err);
      // Continue updating local settings so UI remains responsive
    }
  };

  const onReset = () => setSettings(defaultSettings);

  const onSave = async () => {
    setSaving(true);
    try {
      // Replace with API call to persist settings
      await new Promise((r) => setTimeout(r, 600));
      // eslint-disable-next-line no-console
      console.log('Stock In settings saved:', settings);
      alert('Stock In settings saved');
    } catch (e) {
      alert('Failed to save Stock In settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">Stock In Settings</h2>

      {/* Lists: Vendors, Site Codes */}
      {/* <div className="rounded-lg border p-4 bg-white space-y-4">
        <h3 className="font-medium text-gray-900">Lists</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-2">
            <div className="text-sm text-gray-600">Vendors</div>
            <ChipList items={settings.vendors} onRemove={(item) => removeItem('vendors', item)} />
            <AddInline placeholder="Add vendor" onAdd={(val) => addUnique('vendors', val)} />
          </div>

        </div>
      </div> */}

      {/* Materials */}
      <div className="rounded-lg border p-4 bg-white space-y-4">
        <h3 className="font-medium text-gray-900">Materials</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-2">
            <div className="text-sm text-gray-600">Plant Materials</div>
            <ChipList items={settings.plantMaterials} onRemove={(item) => removeItem('plantMaterials', item)} />
            <AddInline
              placeholder="Add plant material"
              onAdd={async (val) => {
                await createMaterialType(val, 'plant');
                addUnique('plantMaterials', val);
              }}
            />
          </div>
          <div className="space-y-2">
            <div className="text-sm text-gray-600">Site Materials</div>
            <ChipList items={settings.siteMaterials} onRemove={(item) => removeItem('siteMaterials', item)} />
            <AddInline
              placeholder="Add site material"
              onAdd={async (val) => {
                await createMaterialType(val, 'site');
                addUnique('siteMaterials', val);
              }}
            />
          </div>
        </div>
      </div>

      {/* GST Settings */}
      {/* <div className="rounded-lg border p-4 bg-white space-y-4">
        <h3 className="font-medium text-gray-900">GST Settings</h3>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              GST Percentage (%)
            </label>
            <div className="flex items-center gap-4">
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={settings.gstPercentage || 5}
                onChange={(e) => {
                  const value = parseFloat(e.target.value);
                  if (!isNaN(value) && value >= 0 && value <= 100) {
                    update('gstPercentage', value);
                  }
                }}
                className="rounded-md border px-3 py-2 w-32 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="5"
              />
              <span className="text-sm text-gray-600">
                This percentage will be used to calculate GST on the total amount. 
                The calculation will be: Total Amount × (1 + GST%)
              </span>
            </div>
            <div className="text-xs text-gray-500">
              Example: If Total Amount is 10,000 and GST is 5%, the With GST value will be 10,500.00
            </div>
          </div>
        </div>
      </div> */}

      {/* Validation Rules */}
      {/* <div className="rounded-lg border p-4 bg-white space-y-4">
        <h3 className="font-medium text-gray-900">Validation Rules</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-2">
            <div className="text-sm text-gray-600">Require Qty Unit For (Site)</div>
            <ChipList items={settings.requireQtyUnitForSite} onRemove={(item) => removeItem('requireQtyUnitForSite', item)} />
            <AddInline placeholder="Add material key (e.g., cement)" onAdd={(val) => addUnique('requireQtyUnitForSite', val)} />
          </div>
          <div className="flex items-center gap-3">
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={settings.enableWeightFieldsForBT} onChange={(e) => update('enableWeightFieldsForBT', e.target.checked)} />
              <span className="text-gray-700">Enable weight fields for BT</span>
            </label>
          </div>
        </div>
      </div> */}

      <div className="flex items-center gap-3">
        <button
          onClick={onSave}
          disabled={saving}
          className={`px-4 py-2 rounded-md text-white ${saving ? 'bg-primary/70' : 'bg-primary hover:opacity-90'}`}
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
        {/* <button
          onClick={onReset}
          disabled={saving}
          className="px-4 py-2 rounded-md border hover:bg-gray-50"
        >
          Reset to Defaults
        </button> */}
      </div>
    </div>
  );
};

export default StockInSettings;


